/**
 * BotAI - Silnik decyzyjny botów (offline version)
 *
 * Trzy poziomy trudności:
 * - easy: Losowe decyzje
 * - medium: Podstawowe heurystyki
 * - expert: Zaawansowana strategia
 *
 * Offline port: BotTrainingData dependency removed (no super AI model).
 * HeroService.getHeroById stub (hero abilities skipped for bots in offline).
 */

import GameEngine from './GameEngine.js';
import { getArtifactSalvageReward } from '../cardHelpers.js';

// Wagi decyzji „który Artefakt zostaje w slocie" — lustro stałych z backendowego BotAI.js.
// Moc Artefaktu przybliżamy jego ceną; Legendarne nie mają ceny, więc dostają stałą wyżej
// niż najdroższy zwykły (10). Skala: 1 Złoto = 10 pkt, 1 Punkt Chwały = 5 pkt.
const ARTIFACT_KEEP_WEIGHT = 10;
const LEGENDARY_ARTIFACT_POWER = 12;
const DEFAULT_ARTIFACT_POWER = 4;
const FAME_POINT_WEIGHT = 5;
const POOL_GOLD_WEIGHT = 10;

/** Ile warte jest TRZYMANIE tego Artefaktu (działa co turę, więc bije jednorazową rekompensatę). */
function scoreArtifactKeep(card) {
  if (!card) return 0;
  if (card.subtype === 'legendary') return LEGENDARY_ARTIFACT_POWER * ARTIFACT_KEEP_WEIGHT;
  const goldCost = typeof card.cost === 'number' ? card.cost : (card.cost?.gold || 0);
  return (goldCost || DEFAULT_ARTIFACT_POWER) * ARTIFACT_KEEP_WEIGHT;
}

/** Ile bot dostanie ZA ZNISZCZENIE tego Artefaktu. */
function scoreArtifactSalvage(card) {
  const reward = getArtifactSalvageReward(card);
  if (!reward) return 0;
  return reward.fame * FAME_POINT_WEIGHT + reward.gold * POOL_GOLD_WEIGHT;
}

// Stałe poza klasą (getter zamiast `static X = …`): Next.js kompiluje pola statyczne do bloku
// `static {}`, którego nie zna Chrome 92 w Chromecaście / Google TV.
const STATIC_DIFFICULTY_FACTOR = {
  easy: 0.25,
  medium: 0.6,
  expert: 0.9,
};

class BotAI {
  static get DIFFICULTY_FACTOR() { return STATIC_DIFFICULTY_FACTOR; }

  static getGamePhase(game, player) {
    const turn = game.currentTurn || 1;
    if (turn <= 4) return 'early';
    if (turn <= 10) return 'mid';
    if (turn <= 20) return 'late';
    return 'very_late';
  }

  static async getScarcityMultiplier(player, resourceType) {
    const allCardIds = [...player.hand, ...player.deck, ...player.discard];
    if (allCardIds.length === 0) return 1.0;

    let resourceCount = 0;
    for (const cardId of allCardIds) {
      const card = await GameEngine.getCardById(cardId);
      if (card && card.stats && card.stats[resourceType] > 0) resourceCount++;
    }

    const ratio = resourceCount / allCardIds.length;
    if (ratio < 0.1) return 3.0;
    if (ratio < 0.2) return 2.0;
    if (ratio < 0.3) return 1.5;
    return 1.0;
  }

  static getOpponentData(game, player) {
    const opponents = game.players.filter(p => p.userId !== player.userId);
    const maxFame = Math.max(0, ...opponents.map(p => p.fame || 0));
    const myFame = player.fame || 0;
    const fameBehind = maxFame - myFame;
    const opponentsBossCount = opponents.reduce((sum, p) =>
      sum + (p.defeatedBosses?.length || 0) + (p.completedEpicQuests?.length || 0), 0);
    return { maxFame, fameBehind, opponentsBossCount, myFame };
  }

  static async decideCardsToPlay(game, player, difficulty) {
    const hand = [...player.hand];
    if (hand.length === 0) return [];

    const cardData = await Promise.all(
      hand.map(async (cardId) => {
        const card = await GameEngine.getCardById(cardId);
        return card ? { ...card, cardId } : null;
      })
    );

    const validCards = cardData.filter(Boolean);

    if (difficulty === 'easy') {
      return this.shuffleArray(validCards)
        .filter(() => Math.random() > 0.1)
        .map(c => c.cardId);
    }

    const strMultiplier = await this.getScarcityMultiplier(player, 'strength');
    const knoMultiplier = await this.getScarcityMultiplier(player, 'knowledge');

    const scored = validCards.map(card => ({
      card,
      score: this.scoreCardToPlay(card, player, difficulty, strMultiplier, knoMultiplier)
    }));

    return scored
      .filter(s => s.score > -100)
      .sort((a, b) => b.score - a.score)
      .map(s => s.card.cardId);
  }

  static scoreCardToPlay(card, player, difficulty, strMult = 1.0, knoMult = 1.0) {
    if (card.type === 'negative' || card.type === 'curse' ||
        card.id?.startsWith('negative_') || card.id?.startsWith('curse_')) {
      return -50;
    }

    let score = 0;
    const stats = card.stats || {};
    const effects = card.effects || [];

    score += (stats.gold || 0) * 10;
    score += (stats.strength || 0) * 10 * strMult;
    score += (stats.knowledge || 0) * 10 * knoMult;
    score += (stats.purchases || 0) * 15;
    score += (stats.challenges || 0) * 15;
    score += (stats.actions || 0) * 20;
    score += (stats.cards || 0) * 12;

    if (card.type === 'artifact') score += 5;

    const hasDestroy = effects.some(e => e.type?.includes('DESTROY') || e.type === 'DESTROY_WOUND_OR_CURSE');
    if (hasDestroy) {
      const hasNegatives = player.hand.some(id => id.startsWith('negative_') || id.startsWith('curse_'))
        || player.discard.some(id => id.startsWith('negative_') || id.startsWith('curse_'));
      if (hasNegatives) score += 25;
    }

    const fameEffect = effects.find(e => e.type === 'GAIN_FAME');
    if (fameEffect) score += (fameEffect.value || 1) * 5;

    return score;
  }

  static async decidePurchase(game, player, difficulty) {
    const hasExtraPurchase = (player.extraPurchases || 0) > 0;
    const hasMainAction = (player.mainActionsRemaining + (player.bonusActionsThisTurn || 0)) > 0;
    if (!hasExtraPurchase && !hasMainAction) return null;

    const resources = this.getAvailableResources(player);

    const marketCards = await Promise.all(
      game.marketplace.map(async (cardId, index) => {
        const card = await GameEngine.getCardById(cardId);
        return card ? { ...card, cardId, marketIndex: index } : null;
      })
    );

    const validCards = marketCards.filter(Boolean);
    const affordable = validCards.filter(card => {
      const cost = card.cost;
      if (typeof cost === 'number') return resources.gold >= cost;
      if (typeof cost === 'object') {
        return (resources.gold >= (cost.gold || 0)) &&
               (resources.strength >= (cost.strength || 0)) &&
               (resources.knowledge >= (cost.knowledge || 0));
      }
      return false;
    });

    if (affordable.length === 0) return null;

    if (difficulty === 'easy') {
      if (Math.random() > 0.8) return null;
      const randomCard = affordable[Math.floor(Math.random() * affordable.length)];
      return { cardId: randomCard.cardId, cardIndex: randomCard.marketIndex };
    }

    const phase = this.getGamePhase(game, player);
    const strMultiplier = await this.getScarcityMultiplier(player, 'strength');
    const knoMultiplier = await this.getScarcityMultiplier(player, 'knowledge');

    const scored = affordable.map(card => ({
      card,
      score: this.scoreCardToBuy(card, player, game, difficulty, phase, strMultiplier, knoMultiplier)
    })).sort((a, b) => b.score - a.score);

    if (difficulty === 'medium' && scored[0].score > 5) {
      return { cardId: scored[0].card.cardId, cardIndex: scored[0].card.marketIndex };
    }

    if (difficulty === 'expert' && scored[0].score > 2) {
      const threshold = (phase === 'late' || phase === 'very_late') ? 12 : 2;
      if (scored[0].score < threshold) return null;
      return { cardId: scored[0].card.cardId, cardIndex: scored[0].card.marketIndex };
    }

    return null;
  }

  static scoreCardToBuy(card, player, game, difficulty, phase = 'mid', strMult = 1.0, knoMult = 1.0) {
    let score = 0;
    const stats = card.stats || {};
    const cost = typeof card.cost === 'number' ? card.cost : (card.cost?.gold || 0);

    score += (stats.gold || 0) * 3;
    score += (stats.strength || 0) * 3 * strMult;
    score += (stats.knowledge || 0) * 3 * knoMult;
    score += (stats.cards || 0) * 3;
    score += (stats.purchases || 0) * 4;
    score += (stats.challenges || 0) * 4;
    if (stats.fame) score += stats.fame * 5;
    if (card.type === 'artifact') score += 3;
    score -= cost * 0.5;

    if (difficulty === 'expert') {
      const negativeCount = [...player.hand, ...player.discard].filter(id =>
        id.startsWith('negative_') || id.startsWith('curse_')
      ).length;
      const hasDestroy = (card.effects || []).some(e => e.type?.includes('DESTROY'));
      if (hasDestroy && negativeCount > 0) score += negativeCount * 2;
      score += (card.effects?.length || 0) * 0.5;

      if (phase === 'early') {
        score += (stats.gold || 0) * 2;
        score += (stats.cards || 0) * 3;
        score += (stats.actions || 0) * 5;
      } else if (phase === 'mid') {
        score += (stats.strength || 0) * 3 * strMult;
        score += (stats.knowledge || 0) * 3 * knoMult;
      } else {
        if (stats.fame) score += stats.fame * 8;
        if (!stats.fame && (stats.gold || 0) <= 1 && (stats.strength || 0) <= 1 && (stats.knowledge || 0) <= 1) {
          score -= 10;
        }
      }
    }

    return score;
  }

  static async decideChallenge(game, player, difficulty) {
    const hasExtraChallenge = (player.extraChallenges || 0) > 0;
    const hasMainAction = (player.mainActionsRemaining + (player.bonusActionsThisTurn || 0)) > 0;
    if (!hasExtraChallenge && !hasMainAction) return null;

    const resources = this.getAvailableResources(player);
    const challenges = [];

    for (let i = 0; i < game.pathOfCourage.length; i++) {
      const cardId = game.pathOfCourage[i];
      if (!cardId) continue;
      const card = await GameEngine.getCardById(cardId);
      if (!card || card.type === 'event') continue;

      const required = (card.stats?.requiredStrength || card.difficulty || 0);
      if (resources.strength >= required) {
        const allRewards = card.rewards || card.effects || [];
        const fame = allRewards.find(r => r.type === 'GAIN_FAME')?.value || 0;
        challenges.push({
          pathType: 'courage', cardIndex: i, card, cost: required,
          fame, isBoss: card.type === 'epic_boss' || card.subtype === 'boss'
        });
      }
    }

    for (let i = 0; i < game.pathOfWisdom.length; i++) {
      const cardId = game.pathOfWisdom[i];
      if (!cardId) continue;
      const card = await GameEngine.getCardById(cardId);
      if (!card || card.type === 'event') continue;

      const required = (card.stats?.requiredKnowledge || card.difficulty || 0);
      if (resources.knowledge >= required) {
        const allRewards = card.rewards || card.effects || [];
        const fame = allRewards.find(r => r.type === 'GAIN_FAME')?.value || 0;
        challenges.push({
          pathType: 'wisdom', cardIndex: i, card, cost: required,
          fame, isEpicQuest: card.type === 'epic_quest'
        });
      }
    }

    if (challenges.length === 0) return null;

    if (difficulty === 'easy') {
      if (Math.random() > 0.2) return null;
      const random = challenges[Math.floor(Math.random() * challenges.length)];
      return { pathType: random.pathType, cardIndex: random.cardIndex };
    }

    const phase = this.getGamePhase(game, player);
    const opponentData = difficulty === 'expert' ? this.getOpponentData(game, player) : null;

    const scored = challenges.map(ch => ({
      ...ch,
      score: this.scoreChallengeValue(ch, player, difficulty, phase, opponentData)
    })).sort((a, b) => b.score - a.score);

    if (difficulty === 'medium' && scored[0].score > 3) {
      return { pathType: scored[0].pathType, cardIndex: scored[0].cardIndex };
    }
    if (difficulty === 'expert' && scored[0].score > 0) {
      return { pathType: scored[0].pathType, cardIndex: scored[0].cardIndex };
    }

    return null;
  }

  static scoreChallengeValue(challenge, player, difficulty, phase = 'mid', opponentData = null) {
    let score = 0;
    score += challenge.fame * 15;
    if (challenge.isBoss) score += 100;
    if (challenge.isEpicQuest) score += 80;
    if (challenge.fame === 0 && !challenge.isBoss && !challenge.isEpicQuest) score += 15;
    score -= challenge.cost * 0.1;

    const allRewards = challenge.card?.rewards || challenge.card?.effects || [];
    for (const reward of allRewards) {
      if (reward.type === 'GAIN_GOLD') score += (reward.value || 0) * 3;
      if (reward.type === 'GAIN_STRENGTH') score += (reward.value || 0) * 3;
      if (reward.type === 'GAIN_KNOWLEDGE') score += (reward.value || 0) * 3;
      if (reward.type === 'DRAW_CARDS') score += (reward.value || 0) * 4;
      if (reward.type === 'GAIN_FAME') score += (reward.value || 0) * 8;
    }

    if (difficulty === 'expert') {
      if (phase === 'very_late') {
        score += challenge.fame * 50;
        if (challenge.isBoss) score += 150;
        if (challenge.isEpicQuest) score += 120;
      } else if (phase === 'late') {
        score += challenge.fame * 30;
        if (challenge.isBoss) score += 60;
        if (challenge.isEpicQuest) score += 50;
      } else if (phase === 'early') {
        score -= challenge.cost * 0.3;
      }

      if (opponentData) {
        if (opponentData.fameBehind > 5 || phase === 'very_late') {
          score += challenge.fame * 20;
          if (challenge.isBoss) score += 50;
        }
        if (opponentData.opponentsBossCount > 0 && (challenge.isBoss || challenge.isEpicQuest)) {
          score += 60;
        }
      }
    }

    return score;
  }

  static async resolvePendingEffect(pendingEffect, game, player, difficulty) {
    const { type, value, options } = pendingEffect;

    if (type === 'DESTROY_FROM_HAND') return this.chooseCardsToDestroy(player.hand, value || 1, player, difficulty);
    if (type === 'DESTROY_FROM_DISCARD') return this.chooseCardsToDestroy(player.discard, value || 1, player, difficulty);

    if (type === 'DESTROY_WOUND_OR_CURSE') {
      const negatives = [...player.hand, ...player.discard].filter(id =>
        id.startsWith('negative_') || id.startsWith('curse_'));
      if (negatives.length > 0) return { cardId: negatives[0] };
      if (player.hand.length > 0) return { cardId: player.hand[0] };
      return { choice: 'skip' };
    }

    if (type === 'TAKE_FREE_MARKET_CARD') {
      if (options && options.length > 0) return { cardId: options[0] };
      if (game.marketplace.length > 0) return { cardId: game.marketplace[0] };
      return { choice: 'skip' };
    }

    if (type?.startsWith('CHOICE_')) return this.resolveChoiceEffect(type, options, player, difficulty);

    if (type === 'PEEK_ADVENTURE_DECK_RESOLVE') {
      // Bot keeps all cards on top (no card moved to bottom)
      const decisions = {};
      for (const cardId of (options || [])) {
        decisions[cardId] = 'keep';
      }
      return { choice: { decisions } };
    }
    // Wymiana Artefaktu: jeden zostaje, drugi ginie i wypłaca rekompensatę (Legendarny
    // 4 Punkty Chwały, zwykły połowa ceny w Złocie). Lustro `BotAI.resolvePendingEffect`
    // z backendu — CardEffects rozpoznaje wybór po `context.cardId`, nie po słowie 'replace'.
    if (type === 'ARTIFACT_REPLACEMENT_CHOICE') {
      const newCardId = pendingEffect.cardId;
      const oldCardId = (options || []).find(id => id !== newCardId);
      if (!newCardId || !oldCardId) return { cardId: newCardId };
      const [newCard, oldCard] = await Promise.all([
        GameEngine.getCardById(newCardId),
        GameEngine.getCardById(oldCardId)
      ]);
      const keepNew = scoreArtifactKeep(newCard) + scoreArtifactSalvage(oldCard);
      const keepOld = scoreArtifactKeep(oldCard) + scoreArtifactSalvage(newCard);
      return { cardId: keepOld > keepNew ? oldCardId : newCardId };
    }

    if (type === 'LEGENDARY_ARTIFACT_ACQUISITION') {
      const occupantId = player.equippedArtifacts?.[pendingEffect.value] || null;
      if (!occupantId || occupantId === pendingEffect.cardId) return { option: 'equip', choice: 'equip' };
      const [newCard, occupantCard] = await Promise.all([
        GameEngine.getCardById(pendingEffect.cardId),
        GameEngine.getCardById(occupantId)
      ]);
      const equip = scoreArtifactKeep(newCard) + scoreArtifactSalvage(occupantCard);
      const keep = scoreArtifactKeep(occupantCard) + scoreArtifactSalvage(newCard);
      const decision = keep > equip ? 'keep_current' : 'equip';
      return { option: decision, choice: decision };
    }

    if (type === 'LEGENDARY_ARTIFACT_CHOICE') return { option: 'equip', choice: 'equip' };

    if (type === 'DISCARD_FROM_HAND') return this.chooseCardsToDiscard(player.hand, value || 1, player, difficulty);

    if (type === 'ARTIFACT_STAT_BONUS_CHOICE') {
      const resources = this.getAvailableResources(player);
      if (options && options.length > 0) {
        let bestOption = options[0];
        let leastAmount = Infinity;
        for (const opt of options) {
          if ((resources[opt] || 0) < leastAmount) { leastAmount = resources[opt] || 0; bestOption = opt; }
        }
        return { choice: bestOption };
      }
      return { choice: 'gold' };
    }

    if (options && options.length > 0) return { choice: options[0] };
    return { choice: 'skip' };
  }

  static chooseCardsToDestroy(pile, count, player, difficulty) {
    if (!pile || pile.length === 0) return { choice: 'skip' };
    const available = [...pile];
    const toDestroy = [];

    const negatives = available.filter(id => id.startsWith('negative_') || id.startsWith('curse_'));
    for (const neg of negatives) {
      if (toDestroy.length >= count) break;
      toDestroy.push(neg);
    }

    if (toDestroy.length < count) {
      const remaining = available.filter(id => !toDestroy.includes(id));
      for (const id of remaining) {
        if (toDestroy.length >= count) break;
        if (difficulty !== 'easy' && id.startsWith('starter_')) toDestroy.push(id);
        else if (difficulty === 'easy') toDestroy.push(id);
      }
    }

    if (toDestroy.length === 0) return { choice: 'skip' };
    if (toDestroy.length === 1) return { cardId: toDestroy[0] };
    return { cardIds: toDestroy };
  }

  static chooseCardsToDiscard(hand, count, player, difficulty) {
    if (!hand || hand.length === 0) return { choice: 'skip' };
    const toDiscard = [];
    const negatives = hand.filter(id => id.startsWith('negative_') || id.startsWith('curse_'));
    for (const neg of negatives) {
      if (toDiscard.length >= count) break;
      toDiscard.push(neg);
    }
    const remaining = hand.filter(id => !toDiscard.includes(id));
    for (const id of remaining) {
      if (toDiscard.length >= count) break;
      toDiscard.push(id);
    }
    if (toDiscard.length === 0) return { choice: 'skip' };
    if (toDiscard.length === 1) return { cardId: toDiscard[0] };
    return { cardIds: toDiscard };
  }

  static resolveChoiceEffect(effectType, options, player, difficulty) {
    const resources = this.getAvailableResources(player);
    if (effectType === 'CHOICE_GAIN_STRENGTH_1_OR_GAIN_KNOWLEDGE_1' ||
        effectType === 'CHOICE_GAIN_STRENGTH_2_OR_GAIN_KNOWLEDGE_2') {
      return { choice: resources.strength <= resources.knowledge ? 'strength' : 'knowledge' };
    }
    if (effectType === 'CHOICE_GAIN_GOLD_2_OR_GAIN_GOLD_3_TAKE_SHAME') {
      return { choice: difficulty === 'expert' ? 'option1' : (Math.random() > 0.5 ? 'option1' : 'option2') };
    }
    if (options && options.length > 0) return { choice: options[0] };
    return { choice: 'option1' };
  }

  static getAvailableResources(player) {
    return {
      strength: (player.pool?.strength || 0) + (player.resources?.strength || 0),
      knowledge: (player.pool?.knowledge || 0) + (player.resources?.knowledge || 0),
      gold: (player.pool?.gold || 0) + (player.resources?.gold || 0)
    };
  }

  static shuffleArray(array) {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }
}

export default BotAI;
